import { useCallback, useMemo } from 'react'

import { Box, Grid, Stack, Typography } from '@mui/material'

import { ParentSize } from '@visx/responsive'
import { DateTime } from 'luxon'

import LineChart from '@common/components/reports/RedLineReport/LineChart'

import aerosolData from './aerosolData.json'
import cookingData from './cookingData.json'
import {
  canvasStyles,
  headerStyles,
  propertyHeaderStyles,
  titleStyles,
} from './RedLineReport.styles'
import showerData from './showerData.json'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object[]} props.lines
 * @param {boolean} props.loading
 * @param {boolean} props.error
 * @param {number} [props.staticHeight]
 * @param {number} [props.staticWidth]
 * @param {string} props.timezone
 * @param {boolean} [props.hideAxis]
 * @param {React.Component} props.LoadingComponent

 */
function SafeLineChart(props) {
  const {
    lines,
    loading,
    error,
    staticHeight,
    staticWidth,
    timezone,
    hideAxis,
    LoadingComponent,
  } = props

  const CONTAINER_HEIGHT = staticHeight ?? 300

  if (loading) {
    return <LoadingComponent height={CONTAINER_HEIGHT} />
  }

  if (error || !lines) {
    return (
      <Box
        height={CONTAINER_HEIGHT}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="h5">
          Something went wrong. Please contact support.
        </Typography>
      </Box>
    )
  }

  const { isSSR } = globalThis
  if (isSSR) {
    // server side rendering
    return (
      <LineChart
        lines={lines}
        height={CONTAINER_HEIGHT}
        width={staticWidth}
        timezone={timezone}
        hideAxis={hideAxis}
      />
    )
  }

  return (
    <ParentSize>
      {({ width }) => (
        <LineChart
          lines={lines}
          height={CONTAINER_HEIGHT}
          width={width}
          timezone={timezone}
          hideAxis={hideAxis}
        />
      )}
    </ParentSize>
  )
}

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Function} props.onClose
 * @param {Object} props.data.instance
 * @param {string} props.data.instance.id
 * @param {string} props.data.instance.propertyName
 * @param {string} props.data.instance.unitName
 * @param {string} props.data.instance.createdOn
 * @param {Object} [props.data.property]
 * @param {string} [props.data.property.name]
 * @param {string} [props.data.property.timezone]
 * @param {number} [props.data.property.unitCount]
 * @param {string} [props.data.property.propertyType]
 * @param {Object[]} [props.data.probabilityData]
 * @param {React.Component} props.LoadingComponent
 * @param {boolean} [props.loading=false]
 * @param {boolean} [props.error=false]
 */
export default function RedLineReportContent(props) {
  const { data, LoadingComponent, loading = false, error = false } = props
  const { instance, property, probabilityData } = data ?? {}

  const tz = property?.timezone ?? instance?.timeZone
  const unitCount = property?.unitCount ?? instance?.propertyUnitCount
  const propertyType = property?.propertyType ?? instance?.propertyType
  const propertyName = property?.name ?? instance?.propertyName

  const { isSSR } = globalThis

  const messageData = useCallback(
    (chartData) =>
      chartData.map((item, idx) => ({
        value: item,
        ts: DateTime.fromISO(instance?.createdOn, { zone: tz })
          .plus({ minutes: idx + 1 })
          .toMillis(),
      })),
    [instance, property],
  )

  const aerosolChart = useMemo(
    () => [
      {
        key: 'aerosol',
        color: '#c1c1c1',
        data: messageData(aerosolData),
      },
    ],
    [instance],
  )

  const cookingChart = useMemo(
    () => [
      {
        key: 'cooking',
        color: '#c1c1c1',
        data: messageData(cookingData),
      },
    ],
    [instance],
  )

  const showerChart = useMemo(
    () => [
      {
        key: 'shower',
        color: '#c1c1c1',
        data: messageData(showerData),
      },
    ],
    [instance],
  )

  const eventTime = useMemo(
    () =>
      DateTime.fromISO(instance?.createdOn, {
        zone: tz,
      }),
    [property, instance],
  )

  const isPrunit = useMemo(() => unitCount === 1, [property])

  const isHotel = useMemo(() => propertyType === 'HOTEL', [property])

  const exampleChartHeight = 84
  const exampleChartWidth = 329
  const smokeChartHeight = 300
  const smokeChartWidth = 970

  return (
    <div id="printable_canvas" style={canvasStyles}>
      <Typography variant="h4" sx={propertyHeaderStyles}>
        {propertyName}
      </Typography>

      <Typography variant="h4" sx={titleStyles}>
        Smoke Report
      </Typography>

      {instance && (
        <Typography variant="h6" sx={headerStyles}>
          {!isPrunit ? (
            <>
              {`${isHotel ? 'Room' : 'Unit'} ${instance.unitName}`}
              <br />
            </>
          ) : null}
          event confirmed at {eventTime.toLocaleString(DateTime.DATETIME_SHORT)} <br />
        </Typography>
      )}
      <Stack spacing={2}>
        <Typography variant="caption" style={{ lineHeight: 1.5 }} component="div">
          The data in this graph represents the period of time that the air around the
          sensor had a distinctive signature indicative of smoking in sufficient
          quantity to trigger an alert. Note that the actual start/stop time of smoking
          may have been earlier/later depending on the distance between the smoker and
          the sensor and patterns of airflow around the sensor and within the room.
        </Typography>
        <Box sx={{ border: '1px solid #ccc', p: 2, height: 370 }}>
          <SafeLineChart
            lines={
              probabilityData
                ? [
                    {
                      key: 'smoke',
                      color: '#000',
                      data: probabilityData,
                    },
                  ]
                : null
            }
            staticHeight={smokeChartHeight}
            staticWidth={isSSR ? smokeChartWidth : undefined}
            error={error}
            loading={loading}
            timezone={tz}
            LoadingComponent={LoadingComponent}
          />
        </Box>
        <Box>
          <Typography variant="body1" sx={{ pl: 1, pb: 2 }}>
            Representative smoke detection score graphs from other common activities
          </Typography>
          <Grid container spacing={1}>
            <Grid size={{ xs: 4 }}>
              <Typography variant="body2" align="center">
                Aerosols
              </Typography>
              <SafeLineChart
                lines={aerosolChart}
                staticHeight={exampleChartHeight}
                staticWidth={isSSR ? exampleChartWidth : undefined}
                timezone={tz}
                LoadingComponent={LoadingComponent}
                hideAxis
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography variant="body2" align="center">
                Shower
              </Typography>
              <SafeLineChart
                lines={showerChart}
                staticHeight={exampleChartHeight}
                staticWidth={isSSR ? exampleChartWidth : undefined}
                timezone={tz}
                LoadingComponent={LoadingComponent}
                hideAxis
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography variant="body2" align="center">
                Cooking
              </Typography>
              <SafeLineChart
                lines={cookingChart}
                staticHeight={exampleChartHeight}
                staticWidth={isSSR ? exampleChartWidth : undefined}
                timezone={tz}
                LoadingComponent={LoadingComponent}
                hideAxis
              />
            </Grid>
          </Grid>
          <Typography variant="caption" style={{ lineHeight: 1.5 }} component="div">
            The smoke detection technology used by {propertyName} measures several
            aspects of air quality in guest rooms. Measurements from sensors are passed
            to a proprietary algorithm that has been verified by rigorous statistical
            testing. The algorithm is specifically designed to detect combusted or
            vaporized tobacco, marijuana, nicotine products, and e-cigarette devices.
            This report has been generated based on air quality data collected from the
            specific hotel room during this reservation. The data indicates that smoking
            occurred during this period.
          </Typography>
        </Box>
      </Stack>
    </div>
  )
}
